import React from 'react';
import { StandardTableActions } from '../../../components/ui';

import { Column } from '../../../components/DataTable/DataTable';
import { Duration } from './useData';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';

export const moduleConfig = {
  module: {
    title: 'Durations',
    description: 'Manage durations',
    permissionModuleKey: 'duration',
    route: {
      list: '/duration',
      new: '/duration/new',
      detail: (id: number) => `/duration/${id}`,
      edit: (id: number) => `/duration/${id}/edit`,
    },
    api: {
      list: '/p-crm-duration',
      detail: (id: number) => `/p-crm-duration/${id}`,
    },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  search: {
    placeholder: 'Search durations...',
  },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: ['pd_descr', 'pd_min'],
    },
  ],
  filters: {
    fields: [
      {
        key: 'pd_descr',
        label: 'Description',
        placeholder: 'Type to filter description...',
        type: 'text' as const,
        getValue: (item: Duration) => item.pd_descr,
        options: [],
      },
      {
        key: 'pd_min',
        label: 'Minutes',
        placeholder: 'All Minutes',
        type: 'number' as const,
        getValue: (item: Duration) => item.pd_min?.toString(),
        options: [],
      },
    ],
    applyFilter: (items: Duration[], filters: { pd_descr: string[] | string; pd_min: string[] }) => {
      return items.filter((item) => {
        const descrFilter = filters.pd_descr;
        if (typeof descrFilter === 'string' && descrFilter.trim() !== '') {
          const value = (item.pd_descr || '').toLowerCase();
          if (!value.includes(descrFilter.toLowerCase())) return false;
        } else if (Array.isArray(descrFilter) && descrFilter.length > 0) {
          const value = (item.pd_descr || '').toLowerCase();
          const matches = descrFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }
        
        // Minutes filter
        if (filters.pd_min.length > 0) {
          const value = item.pd_min?.toString() || '';
          const matches = filters.pd_min.some((filter) => {
            return value === filter || filter === value;
          });
          if (!matches) return false;
        }
        
        return true;
      });
    },
  },
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Duration',
        showTitle: false,
        variant: 'primary' as const,
        href: '/duration/new',
      },
    ],
  },
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: Duration) => {
      window.location.href = `/duration/${item.id}`;
    },
    keyExtractor: (item: Duration) => item.id,
  },
  emptyStates: {
    noData: 'Get started by creating a new duration.',
    noSearchResults: 'No durations found matching your search criteria.',
    noFilterResults: 'No durations match the selected filters.',
  },
  delete: {
    title: 'Delete Duration',
    message: 'Are you sure you want to delete this duration? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete duration. Please try again.',
    handler: async (id: number) => {
      const { deleteDuration } = await import('../../../services/mutations');
      await deleteDuration(id);
    },
  },
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },
  detail: {
    subtitle: 'Duration Details',
    notFoundTitle: 'Duration not found',
    notFoundMessage: 'The duration you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Durations',
    defaultTitle: 'Unnamed Duration',
    getTitle: (item: Duration) => {
      return item.pd_descr || `Duration #${item.id}`;
    },
  },
  form: {
    new: {
      title: 'New Duration',
      subtitle: 'Add a new duration',
      submitText: 'Create Duration',
    },
    edit: {
      title: 'Edit Duration',
      subtitle: 'Update duration information',
      submitText: 'Update Duration',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading duration...',
    errorMessage: 'Failed to save duration. Please try again.',
    create: async (data: Partial<Duration>) => {
      const { createDuration } = await import('../../../services/mutations');
      return await createDuration(data);
    },
    update: async (id: number, data: Partial<Duration>) => {
      const { updateDuration } = await import('../../../services/mutations');
      return await updateDuration(id, data);
    },
  },
  fields: [
    {
      name: 'pd_descr',
      label: 'Description',
      type: 'text',
      required: true,
      placeholder: 'Enter duration description',
      validation: {
        required: true,
        minLength: 1,
        maxLength: 255,
      },
    },
    {
      name: 'pd_min',
      label: 'Minutes',
      type: 'number',
      placeholder: 'Enter minutes (optional)',
      validation: {
        min: 0,
      },
    },
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Duration Information',
        icon: '📝',
        fields: ['pd_descr', 'pd_min'],
        columns: 2,
        collapsible: false,
      },
    ],
  } as FormLayout,
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<Duration>[] => [
  {
    key: 'id',
    header: 'ID',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
  {
    key: 'pd_descr',
    header: 'Description',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
  {
    key: 'pd_min',
    header: 'Minutes',
    sortable: true,
    render: (value, row) => (
      <span className="text-sm text-gray-900">{row.pd_min !== undefined ? `${row.pd_min} min` : '-'}</span>
    ),
  },
];

export const getDetailSections = (
  item: Duration,
  formatDate: (dateString?: string) => string
) => {
  return [
    {
      title: 'Basic Information',
      icon: '📝',
      fields: [
        {
          label: 'Description',
          value: item.pd_descr || '-',
        },
        {
          label: 'Minutes',
          value: item.pd_min !== undefined ? `${item.pd_min} min` : '-',
        },
      ],
    },
  ];
};

export const getStatusBadges = (item: Duration) => {
  return null;
};

export const getInitialFormData = (): Partial<Duration> => {
  return {
    pd_descr: '',
    pd_min: undefined,
  };
};

export const mapItemToFormData = (item: Duration): Partial<Duration> => {
  return {
    pd_descr: item.pd_descr || '',
    pd_min: item.pd_min,
  };
};

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    default:
      return null;
  }
};

export const getTableActions = (
  item: Duration,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};

